import {
  DEMO_PASSCODE_HEADER,
  type AccountResponse,
  type ApiErrorBody,
  type CreateCustomerSwapRequest,
  type CustomerSwapQuoteRequest,
  type CustomerSwapQuoteResponse,
  type CustomerSwapResponse,
  type DepositRequest,
  type DepositResponse,
  type EnsureCustomerProvisionedResult,
  type NetworksResponse,
  type SessionRequest,
  type SessionResponse,
  type WithdrawRequest,
  type WithdrawResponse,
} from '@shared/contract';

export class DemoApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** Provider's own sanitized reason — present only for QUOTE_REJECTED. */
  readonly providerMessage?: string;

  constructor(code: string, message: string, status: number, providerMessage?: string) {
    super(message);
    this.name = 'DemoApiError';
    this.code = code;
    this.status = status;
    this.providerMessage = providerMessage;
  }
}

export type PasscodeGetter = () => string | null | undefined;

export interface DemoApi {
  createSession: (body: SessionRequest) => Promise<SessionResponse>;
  getNetworks: () => Promise<NetworksResponse>;
  provision: (body: {
    customerId: string;
    mintId: number;
  }) => Promise<EnsureCustomerProvisionedResult>;
  deposit: (body: DepositRequest) => Promise<DepositResponse>;
  getAccount: (customerId: string) => Promise<AccountResponse>;
  withdraw: (body: WithdrawRequest) => Promise<WithdrawResponse>;
  quoteCustomerSwap: (
    body: CustomerSwapQuoteRequest,
  ) => Promise<CustomerSwapQuoteResponse>;
  createCustomerSwap: (
    body: CreateCustomerSwapRequest,
  ) => Promise<CustomerSwapResponse>;
  getCustomerSwapStatus: (swapId: string) => Promise<CustomerSwapResponse>;
}

function assertApiBase(base: string | undefined): asserts base is string {
  if (!base) {
    throw new Error('VITE_DEMO_API_BASE is not set');
  }
}

function normalizeBase(base: string): string {
  return base.replace(/\/+$/, '');
}

async function parseError(res: Response): Promise<DemoApiError> {
  let code = 'INTERNAL_ERROR';
  let message = res.statusText || 'Request failed';
  let providerMessage: string | undefined;
  try {
    const data = (await res.json()) as ApiErrorBody;
    if (data?.error?.code) code = data.error.code;
    if (data?.error?.message) message = data.error.message;
    if (data?.error?.providerMessage) providerMessage = data.error.providerMessage;
  } catch {
    // keep defaults
  }
  return new DemoApiError(code, message, res.status, providerMessage);
}

export function createDemoApi(options: {
  getPasscode: PasscodeGetter;
  baseUrl?: string;
}): DemoApi {
  const baseUrl = normalizeBase(options.baseUrl ?? import.meta.env.VITE_DEMO_API_BASE ?? '');

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    assertApiBase(baseUrl);
    const passcode = options.getPasscode();
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (passcode) {
      headers[DEMO_PASSCODE_HEADER] = passcode;
    }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!res.ok) {
      throw await parseError(res);
    }

    return (await res.json()) as T;
  }

  return {
    createSession: (body) => request<SessionResponse>('POST', '/session', body),
    getNetworks: () => request<NetworksResponse>('GET', '/networks'),
    provision: (body) =>
      request<EnsureCustomerProvisionedResult>('POST', '/provision', body),
    deposit: (body) => request<DepositResponse>('POST', '/deposit', body),
    getAccount: (customerId) =>
      request<AccountResponse>(
        'GET',
        `/account?customerId=${encodeURIComponent(customerId)}`,
      ),
    withdraw: (body) => request<WithdrawResponse>('POST', '/withdraw', body),
    quoteCustomerSwap: (body) =>
      request<CustomerSwapQuoteResponse>('POST', '/customer-swap/quote', body),
    createCustomerSwap: (body) =>
      request<CustomerSwapResponse>('POST', '/customer-swap', body),
    getCustomerSwapStatus: (swapId) =>
      request<CustomerSwapResponse>(
        'GET',
        `/customer-swap-status?swapId=${encodeURIComponent(swapId)}`,
      ),
  };
}
